import { SEND_QUEUES, type DataDatabase } from './data-db'

/**
 * - `removed` — le contrôle a quitté la tablette ;
 * - `served` — rien n'est retiré : sa saisie n'est pas marquée hors tournée, le contrôle existe
 *   encore au bureau ;
 * - `sending` — rien n'est retiré : un de ses envois est en vol, le retirer n'empêcherait pas
 *   qu'il arrive. Le geste se refait une fois l'envoi abouti.
 */
export type DiscardResult = 'removed' | 'served' | 'sending'

/**
 * Retire **à la main** un contrôle que la descente garde alors que le serveur ne le sert plus
 * (#668) : sa saisie, ses pièces ou un envoi pas encore reçu le protègent de la purge
 * (`offline/pull.ts`), et sans ce geste il resterait sur la tablette — transmissible pour rien
 * après une remise à zéro des données au bureau.
 *
 * ⚠️ **Acte irréversible** : la demande, le brouillon, les pièces et les envois **non reçus** des
 * quatre files sont supprimés de la base de l'équipe. L'écran le confirme avant d'appeler.
 * Les envois déjà reçus restent : c'est l'historique de « Notifications », et le serveur les tient.
 *
 * ⚠️ **Seulement hors tournée** (`outOfTourAt`, posé par une descente complète, #147) : vérifié
 * ici, dans la transaction qui supprime, et non sur la foi de l'écran — une tournée redescendue
 * entre l'affichage et le geste a pu rendre la demande à l'équipe.
 */
export async function discardOutOfTour(
  db: DataDatabase,
  inquiryId: string,
): Promise<DiscardResult> {
  const tables = [db.inquiries, db.drafts, db.media, ...SEND_QUEUES.map((queue) => db[queue])]

  return db.transaction('rw', tables, async () => {
    const draft = await db.drafts.get(inquiryId)
    if (draft?.outOfTourAt == null) return 'served'

    const unsent = await Promise.all(
      SEND_QUEUES.map(async (queue) => ({
        queue,
        entries: (await db[queue].where('state').notEqual('sent').toArray()).filter(
          (entry) => entry.inquiryId === inquiryId,
        ),
      })),
    )
    if (unsent.some(({ entries }) => entries.some((entry) => entry.state === 'sending'))) {
      return 'sending'
    }

    for (const { queue, entries } of unsent) {
      await db[queue].bulkDelete(entries.map((entry) => entry.id))
    }
    await db.media.where('inquiryId').equals(inquiryId).delete()
    await db.drafts.delete(inquiryId)
    await db.inquiries.delete(inquiryId)

    return 'removed'
  })
}
